import { Injectable, inject } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { getQueryParam } from "../helpers/url.helper";
import { ThemeService } from "./theme.service";

export interface ILayoutConf {
  navigationPos?: string; // side, top
  sidebarStyle?: string; // full, compact, closed
  sidebarCompactToggle?: boolean; // sidebar expandable on hover
  sidebarColor?: string; // Sidebar background colour
  dir?: string; // ltr, rtl
  isMobile?: boolean; // updated automatically
  useBreadcrumb?: boolean; // Breadcrumb enabled/disabled
  breadcrumb?: string; // simple, title
  topbarFixed?: boolean; // Fixed header
  footerFixed?: boolean; // Fixed Footer
  topbarColor?: string; // Header background colour
  footerColor?: string; // Footer background colour
  matTheme?: string; // Active customer q-* Material theme class
}
export interface ILayoutChangeOptions {
  duration?: number;
  transitionClass?: boolean;
}
interface IAdjustScreenOptions {
  browserEvent?: any;
  route?: string;
}

@Injectable({
  providedIn: "root"
})
export class LayoutService {
  private themeService = inject(ThemeService);

  public layoutConf: ILayoutConf = {} as ILayoutConf;
  layoutConfSubject = new BehaviorSubject<ILayoutConf>(this.layoutConf);
  layoutConf$ = this.layoutConfSubject.asObservable();
  public isMobile: boolean;
  public currentRoute: string;
  public fullWidthRoutes = ["shop"];

  constructor() {
    this.setAppLayout(
      //******** SET YOUR LAYOUT OPTIONS HERE *********
      {
        navigationPos: "side", // side, top
        sidebarStyle: "full", // full, compact, closed
        sidebarColor: "slate", // Sidebar background colour
        sidebarCompactToggle: false, // applied when "sidebarStyle" is "compact"
        dir: "ltr", // ltr, rtl
        useBreadcrumb: true,
        topbarFixed: false,
        footerFixed: false,
        topbarColor: "white", // Header background colour
        footerColor: "slate", // Footer background colour
        matTheme: "", // Base customer red-primary theme; q-* variants are scoped where required
        breadcrumb: "simple", // simple, title
      }
    );
  }

  setAppLayout(layoutConf: ILayoutConf) {
    this.layoutConf = { ...this.layoutConf, ...layoutConf };
    this.applyMatTheme(this.layoutConf.matTheme);

    //******* Only for demo purpose ***
    this.setLayoutFromQuery();
    //**********************
  }

  publishLayoutChange(lc: ILayoutConf, _opt: ILayoutChangeOptions = {}) {
    const hasThemeUpdate = typeof lc.matTheme === "string";
    const nextTheme = hasThemeUpdate
      ? this.themeService.resolveThemeName(lc.matTheme)
      : undefined;

    if (hasThemeUpdate && this.layoutConf.matTheme !== nextTheme) {
      this.themeService.changeTheme(this.layoutConf.matTheme, nextTheme);
    }

    this.layoutConf = Object.assign(this.layoutConf, {
      ...lc,
      ...(hasThemeUpdate ? { matTheme: nextTheme } : {}),
    });
    this.layoutConfSubject.next(this.layoutConf);
  }

  applyMatTheme(theme?: string) {
    this.themeService.applyMatTheme(theme ?? this.layoutConf.matTheme);
  }

  setLayoutFromQuery() {
    const layoutConfString = getQueryParam("layout");
    const prevTheme = this.layoutConf.matTheme;
    try {
      const queriedLayout = JSON.parse(layoutConfString);
      const hasQueriedTheme = typeof queriedLayout?.matTheme === "string";
      const queriedTheme = hasQueriedTheme
        ? this.themeService.resolveThemeName(queriedLayout.matTheme)
        : undefined;

      this.layoutConf = {
        ...this.layoutConf,
        ...queriedLayout,
        ...(hasQueriedTheme ? { matTheme: queriedTheme } : {}),
      };

      if (hasQueriedTheme && prevTheme !== queriedTheme) {
        this.themeService.changeTheme(prevTheme, queriedTheme);
      }
    } catch {}
  }

  adjustLayout(options: IAdjustScreenOptions = {}) {
    let sidebarStyle: string;
    this.isMobile = this.isSm();
    this.currentRoute = options.route || this.currentRoute;
    sidebarStyle = this.isMobile ? "closed" : "full";

    if (this.currentRoute) {
      this.fullWidthRoutes.forEach(route => {
        if (this.currentRoute.indexOf(route) !== -1) {
          sidebarStyle = "closed";
        }
      });
    }

    this.publishLayoutChange({
      isMobile: this.isMobile,
      sidebarStyle: sidebarStyle
    });
  }
  isSm() {
    return window.matchMedia(`(max-width: 959px)`).matches;
  }
}
